import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../lib/admin-utils';
import { useAdmin } from '../../context/AdminContext';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Trash2, Loader2 } from 'lucide-react';
import { MembersSkeleton } from './skeletons';

const ASSIGNABLE_ROLES = ['owner', 'admin', 'editor', 'viewer'] as const;

const roleBadgeVariant = (role: string) => {
  switch (role) {
    case 'owner':
      return 'default' as const;
    case 'admin':
      return 'secondary' as const;
    default:
      return 'outline' as const;
  }
};

export default function MembersPage() {
  const { t } = useTranslation();
  const { email: authEmail } = useAuth();
  const {
    loading,
    selectedTenant,
    members,
    canManageMembers,
    addOrUpdateMember,
    removeMember,
    setNotice,
  } = useAdmin();

  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<'admin' | 'editor' | 'viewer'>('viewer');
  const [savingMember, setSavingMember] = useState(false);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  const currentUserEmail = (authEmail ?? '').toLowerCase();
  const currentUserRole = selectedTenant?.role;

  const handleMemberSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      setSavingMember(true);
      await addOrUpdateMember(memberEmail, memberRole);
      setMemberEmail('');
      setNotice({ type: 'success', message: t('members.updated') });
    } catch (error) {
      const message = error instanceof Error ? error.message : t('members.errorUpdate');
      setNotice({ type: 'error', message });
    } finally {
      setSavingMember(false);
    }
  };

  const handleRoleChange = async (email: string, newRole: string) => {
    try {
      setBusyEmail(email);
      await addOrUpdateMember(email, newRole as 'admin' | 'editor' | 'viewer');
      setNotice({ type: 'success', message: t('members.roleUpdated', { email, role: newRole }) });
    } catch (error) {
      const message = error instanceof Error ? error.message : t('members.errorUpdateRole');
      setNotice({ type: 'error', message });
    } finally {
      setBusyEmail(null);
    }
  };

  const handleRemoveMember = async (email: string) => {
    if (!window.confirm(t('members.confirmRemove', { email }))) return;

    try {
      setBusyEmail(email);
      await removeMember(email);
      setNotice({ type: 'success', message: t('members.removed', { email }) });
    } catch (error) {
      const message = error instanceof Error ? error.message : t('members.errorRemove');
      setNotice({ type: 'error', message });
    } finally {
      setBusyEmail(null);
    }
  };

  if (!selectedTenant) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('common.noWorkspace')}</CardTitle>
          <CardDescription>{t('members.noWorkspaceDesc')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (loading && members.length === 0) {
    return <MembersSkeleton />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('members.cardTitle')}</CardTitle>
        <CardDescription>{t('members.cardDesc', { name: selectedTenant.tenantName })}</CardDescription>
      </CardHeader>

      <CardContent>
        {!canManageMembers && (
          <p className="text-sm text-muted-foreground py-8 text-center">{t('members.roleNotice', { role: selectedTenant.role })}</p>
        )}

        {canManageMembers && (
          <>
            <form className="mb-4 flex flex-col gap-3 sm:flex-row" onSubmit={handleMemberSubmit}>
              <Input
                type="email"
                aria-label="Member email"
                placeholder={t('members.emailPlaceholder')}
                value={memberEmail}
                onChange={(event) => setMemberEmail(event.target.value)}
                required
              />
              <select
                aria-label="Member role"
                value={memberRole}
                onChange={(event) => setMemberRole(event.target.value as 'admin' | 'editor' | 'viewer')}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm sm:w-auto"
              >
                <option value="admin">{t('members.roleAdmin')}</option>
                <option value="editor">{t('members.roleEditor')}</option>
                <option value="viewer">{t('members.roleViewer')}</option>
              </select>
              <Button type="submit" className="w-full sm:w-auto" disabled={savingMember}>
                {savingMember ? t('members.saving') : t('members.addOrUpdate')}
              </Button>
            </form>

            {members.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">{t('members.emptyMembers')}</p>
            ) : (
              <div className="border rounded-lg divide-y">
                {members.map((member) => {
                  const isSelf = member.email.toLowerCase() === currentUserEmail;
                  const isOwner = member.role === 'owner';
                  const busy = busyEmail === member.email;

                  // Only owner can change another owner's role; can't change own role
                  const canChangeRole = !isSelf && !(isOwner && currentUserRole !== 'owner');
                  const canRemove = !isSelf && !(isOwner && currentUserRole !== 'owner');

                  return (
                    <div key={member.email} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">{member.email}</p>
                          {isSelf && (
                            <Badge variant="outline" className="text-[10px] shrink-0">{t('members.youBadge')}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{formatDate(member.createdAt)}</p>
                      </div>

                      <div className="flex items-center gap-2 shrink-0 sm:ml-2">
                        {canChangeRole ? (
                          <select
                            aria-label={t('members.roleAriaLabel', { email: member.email })}
                            value={member.role}
                            disabled={busy}
                            onChange={(event) => handleRoleChange(member.email, event.target.value)}
                            className="rounded-md border border-input bg-background px-2 py-1 text-xs font-medium"
                          >
                            {ASSIGNABLE_ROLES.map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        ) : (
                          <Badge variant={roleBadgeVariant(member.role)}>{member.role}</Badge>
                        )}

                        {canRemove && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            disabled={busy}
                            onClick={() => handleRemoveMember(member.email)}
                            title={t('members.removeTooltip', { email: member.email })}
                          >
                            {busy ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
